import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Empty, Select } from "../hr/ui";
import { Sev, Spinner, Tabs } from "./mui";
import { Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AlertsCenter() {
  const { locationId } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [severity, setSeverity] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("open");

  const load = useCallback(() => {
    api.get("/management/alerts", { params: {
      location_id: locationId || undefined, severity: severity || undefined,
      source: source || undefined, status } })
      .then(setData).catch((e) => toast.error(formatApiError(e)));
  }, [locationId, severity, source, status]);

  useEffect(() => { setData(null); load(); }, [load]);

  const ack = async (a) => {
    try {
      await api.post(`/management/alerts/${encodeURIComponent(a.id)}/acknowledge`);
      toast.success("Alert acknowledged");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <Spinner text="Aggregating alerts across every module…" />;

  return (
    <div className="space-y-5" data-testid="mgmt-alerts-page">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: HC.border }} data-testid="alerts-total">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: HC.soft }}>Total Open</div>
          <div className="font-brand font-bold text-[24px]" style={{ color: HC.text }}>{data.total}</div>
        </div>
        {["critical", "high", "medium", "low"].map((s) => (
          <button key={s} onClick={() => setSeverity(severity === s ? "" : s)} data-testid={`alerts-count-${s}`}
            className="rounded-2xl bg-white border p-4 text-left transition-shadow hover:shadow-[0_6px_20px_rgba(59,34,34,0.07)]"
            style={{ borderColor: severity === s ? HC.burgundy : HC.border }}>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: HC.soft }}>{s}</div>
            <div className="font-brand font-bold text-[24px]" style={{ color: HC.text }}>{data.counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <Card testid="mgmt-alerts-list">
        <SectionHead title="Unified exceptions" sub={`Priority-scored across ${Object.keys(data.by_source).length} modules`}
          right={
            <div className="flex flex-wrap gap-2">
              <Select value={source} onChange={(e) => setSource(e.target.value)} className="w-40" data-testid="alerts-source-filter">
                <option value="">All sources</option>
                {Object.keys(data.by_source).map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-36" data-testid="alerts-severity-filter">
                <option value="">All severities</option>
                {["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          } />
        <div className="mb-3">
          <Tabs testid="alerts-status-tabs" value={status} onChange={setStatus}
            tabs={[["open", "Open"], ["acknowledged", "Acknowledged"], ["all", "All"]]} />
        </div>
        <div className="space-y-2">
          {data.alerts.length ? data.alerts.map((a) => (
            <div key={a.id} className="rounded-xl border p-3 flex flex-col md:flex-row md:items-center gap-3"
              style={{ borderColor: HC.border, borderLeft: `4px solid ${a.severity === "critical" ? HC.red : a.severity === "high" ? HC.amber : HC.border}` }}
              data-testid={`alert-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sev value={a.severity} testid={`alert-sev-${a.id}`} />
                  <span className="text-sm font-semibold" style={{ color: HC.text }}>{a.title}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: HC.borderSoft, color: HC.muted }}>{a.source_module}</span>
                </div>
                <div className="text-[12px] mt-1" style={{ color: HC.soft }}>
                  {a.description} · {a.location_name} · {fmtDateTime(a.created_at)} · score {a.priority_score}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {a.related_record?.link && (
                  <Btn variant="outline" onClick={() => navigate(a.related_record.link)} data-testid={`alert-open-${a.id}`}>
                    <ExternalLink size={14} /> Open
                  </Btn>
                )}
                {a.status === "open" && (
                  <Btn variant="primary" onClick={() => ack(a)} data-testid={`alert-ack-${a.id}`}>
                    <Check size={14} /> Acknowledge
                  </Btn>
                )}
              </div>
            </div>
          )) : <Empty text="No alerts match these filters" />}
        </div>
      </Card>
    </div>
  );
}
